// Utility functions for date/time handling with Indian timezone

const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Format date in Indian timezone
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateIST = (date, options = {}) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-IN', { 
    timeZone: IST_TIMEZONE,
    ...options 
  });
};

/**
 * Format time in Indian timezone
 * @param {string} time - Time string (HH:MM format)
 * @returns {string} Formatted time string
 */
export const formatTimeIST = (time) => {
  if (!time) return '';
  // Time is already stored in IST, just return as is
  return time;
};

/**
 * Format date and time together in Indian timezone
 * @param {string|Date} date - Date to format
 * @param {string} time - Time string (HH:MM format)
 * @returns {string} Formatted date-time string
 */
export const formatDateTimeIST = (date, time) => {
  if (!date) return '';
  const dateStr = formatDateIST(date, { month: 'short', day: 'numeric' });
  const timeStr = formatTimeIST(time);
  return timeStr ? `${dateStr}, ${timeStr}` : dateStr;
};

/**
 * Get today's date in YYYY-MM-DD format in IST
 * @returns {string} Today's date in IST
 */
export const getTodayIST = () => {
  const now = new Date();
  return now.toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE }); // en-CA gives YYYY-MM-DD format
};

/**
 * Check if a date is today in IST
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is today in IST
 */
export const isTodayIST = (date) => {
  if (!date) return false;
  const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
  return dateStr === getTodayIST();
};

/**
 * Format date for display with weekday
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date with weekday
 */
export const formatDateWithWeekday = (date) => {
  if (!date) return '';
  return formatDateIST(date, { weekday: 'short', month: 'short', day: 'numeric' });
};

/**
 * Format date for display with full weekday
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date with full weekday
 */
export const formatDateWithFullWeekday = (date) => {
  if (!date) return '';
  return formatDateIST(date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};
